import React, { useMemo } from "react";
import { useParams, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  getProductBySku,
  getProductByFamilyAndSlug,
  getRelatedProducts,
  getQuotePath,
  getProductPath,
} from "../config/products";
import { getRangeBySlug, getRangeForProduct, getRangePath } from "../config/ranges";
import { getCategoryName } from "../config/categories";
import StatusBadge from "../components/product/StatusBadge";
import ProductGalleryPlaceholder from "../components/product/ProductGalleryPlaceholder";
import ProductDownloads from "../components/product/ProductDownloads";
import CertificationStatement from "../components/product/CertificationStatement";
import ProductCard from "../components/product/ProductCard";
import Footer from "../components/Footer";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { getPrimaryProductImage } from "../lib/productMedia";

/**
 * Product variant detail — Product Master driven.
 * Routes: /products/:rangeSlug/:variantSlug  (and legacy /products/:sku via range redirect)
 */
export default function ProductDetailPage() {
  const { rangeSlug, variantSlug } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();

  const product = useMemo(() => {
    if (rangeSlug && variantSlug) {
      return getProductByFamilyAndSlug(rangeSlug, variantSlug);
    }
    return null;
  }, [rangeSlug, variantSlug]);

  const canonicalPath = useMemo(
    () => (product && !product.href ? getProductPath(product) : null),
    [product]
  );

  const range = useMemo(
    () => (product ? getRangeForProduct(product) : getRangeBySlug(rangeSlug)),
    [product, rangeSlug]
  );

  const related = useMemo(
    () => (product ? getRelatedProducts(product.sku) : []),
    [product]
  );

  const heroImage = useMemo(() => getPrimaryProductImage(product), [product]);

  const legacySkuProduct = useMemo(() => {
    if (product || !rangeSlug) return null;
    if (getRangeBySlug(rangeSlug)) return null;
    return getProductBySku(rangeSlug);
  }, [product, rangeSlug]);

  useDocumentMeta({
    title: product?.seo?.title || (product ? `${product.name} | DEBSUN` : "Product | DEBSUN"),
    description: product?.seo?.description || product?.shortDescription || product?.description,
  });

  if (legacySkuProduct) {
    return <Navigate to={getProductPath(legacySkuProduct)} replace />;
  }

  if (canonicalPath && location.pathname.toLowerCase() !== canonicalPath.toLowerCase()) {
    return <Navigate to={canonicalPath} replace />;
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-40 text-center" data-testid="product-not-found">
        <div className="display display-section text-4xl">Product not found.</div>
        <Link
          to="/products"
          className="text-[color:var(--gold)] underline text-sm mt-4 inline-block"
        >
          Back to catalogue
        </Link>
      </div>
    );
  }

  const specs = Array.isArray(product.specifications)
    ? product.specifications
    : Object.entries(product.specifications || {}).map(([label, value]) => ({
        label,
        value,
      }));

  const quoteTo = getQuotePath(product, { authenticated: Boolean(user?.id) });
  const rangeTo = range ? getRangePath(range) : "/products";

  return (
    <div className="min-h-screen pt-28" data-testid="product-detail-page">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-24">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <button
            onClick={() => nav(-1)}
            data-testid="product-back-btn"
            className="inline-flex items-center gap-2 font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {range && (
            <Link
              to={rangeTo}
              className="font-mono-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground hover:text-[color:var(--gold)]"
            >
              {range.name}
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              className="relative overflow-hidden clip-frame border border-border bg-secondary/20"
              data-testid="product-hero-image"
            >
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={product.name}
                  className="w-full aspect-[4/3] object-cover object-center"
                />
              ) : (
                <ProductGalleryPlaceholder />
              )}
              <div className="absolute top-4 left-4 font-mono-2 text-[10px] tracking-[0.3em] uppercase text-foreground bg-background/80 backdrop-blur px-3 py-1 border border-border">
                {product.subcategory || getCategoryName(product.category)}
              </div>
              <div className="absolute bottom-4 right-4 font-mono-2 text-[10px] text-foreground bg-background/80 backdrop-blur px-3 py-1 border border-border">
                {product.sku}
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)]">
                § {range?.name || product.subcategory || getCategoryName(product.category)}
              </div>
              <StatusBadge status={product.status} />
            </div>
            <h1 className="display display-section text-4xl md:text-5xl leading-[0.95] tracking-[-0.03em]">
              {product.name}
            </h1>
            <div className="mt-3 font-mono-2 text-sm text-muted-foreground">
              {product.sku} · {product.brand}
              {product.wattage ? ` · ${product.wattage}` : ""}
            </div>
            <p className="mt-6 text-base text-steel-text leading-relaxed">
              {product.description}
            </p>

            <div className="mt-8 hairline" />

            <div className="mt-8">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                § Technical Specifications
              </div>
              <ul className="space-y-2" data-testid="product-specs">
                {specs.map((s) => (
                  <li
                    key={typeof s === "string" ? s : s.label}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span className="w-1.5 h-1.5 mt-2 bg-[color:var(--gold)] shrink-0" />
                    <span className="text-steel-text">
                      {typeof s === "string" ? (
                        s
                      ) : (
                        <>
                          <span className="text-foreground">{s.label}:</span> {s.value}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                § Features
              </div>
              <ul className="space-y-2">
                {product.features?.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <Check
                      size={14}
                      className="text-[color:var(--teal)] mt-1 shrink-0"
                    />
                    <span className="text-steel-text">{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                § Applications
              </div>
              <div className="flex flex-wrap gap-2">
                {product.applications?.map((a) => (
                  <span
                    key={a}
                    className="px-3 py-1.5 text-xs border border-border rounded-sm font-mono-2 uppercase tracking-widest"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {product.quoteEnabled ? (
                <Link
                  to={quoteTo}
                  data-testid="product-quote-btn"
                  className="btn-gold clip-frame-sm px-6 py-4 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
                >
                  Request a Quote <ArrowUpRight size={14} />
                </Link>
              ) : product.href ? (
                <Link
                  to={product.href}
                  className="btn-gold clip-frame-sm px-6 py-4 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
                >
                  Learn More <ArrowUpRight size={14} />
                </Link>
              ) : null}

              <Link
                to="/#contact"
                data-testid="product-engineering-support-btn"
                className="btn-outline clip-frame-sm px-6 py-4 text-sm uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
              >
                Talk to Engineering
              </Link>
            </div>

            <div className="mt-10 p-5 border border-border bg-secondary/40 text-xs text-muted-foreground font-mono-2 leading-relaxed">
              {product.priceVisible
                ? "Prices shown are indicative and subject to confirmation."
                : "This product operates in Quote Mode. Pricing, lead time and configuration depend on quantity, site conditions and engineering scope."}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <ProductDownloads downloads={product.downloads} />
        </div>

        <div className="mt-10">
          <CertificationStatement />
        </div>

        <div
          className="mt-10 border border-border bg-secondary/30 p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
          data-testid="need-engineering-support"
        >
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              § Need Engineering Support?
            </div>
            <p className="text-sm text-steel-text leading-relaxed max-w-xl">
              Discuss application fit, configuration options and project-specific
              validation requirements with the DEBSUN engineering team.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {product.quoteEnabled && (
              <Link
                to={quoteTo}
                className="btn-gold clip-frame-sm px-5 py-3 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
              >
                Request Quote <ArrowUpRight size={14} />
              </Link>
            )}
            <Link
              to="/#contact"
              className="btn-outline clip-frame-sm px-5 py-3 text-[11px] uppercase tracking-widest font-mono-2"
            >
              Contact
            </Link>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              § Related Products
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p, i) => (
                <ProductCard key={p.sku} product={p} index={i} mode="link-only" />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
